import {useTranslation} from 'react-i18next';
import config from '../../../../../app/config';
import React, {useEffect} from 'react';
import _ from 'lodash';
import moment from 'moment/moment';
import * as _debounce from 'lodash/debounce';
import div from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import InformationBox from '../../../../../components/other/informationBox';
import PropTypes from 'prop-types';
import {emitChangedWait, obsStatuses, obsTypes} from '../obsUtils';
import renderAcceptSection from './renderAcceptSection';
import AgyleDictionaryField from '../../../../../components/ui/selector/agyleDictionaryField';
import {FormLabel} from '@mui/material';
import {obsStyles} from '../obsStyle';

function ObsConsciousness(props) {
    const classes = obsStyles();
    const {t} = useTranslation();
    const {type, ageRangeYears} = props;
    const allowDefaultValue = config?.observations?.defaultValues;
    const disableAcceptButtons = config?.observations?.disableAcceptButtons;

    const [status, setStatus] = React.useState(props.status || obsStatuses.new);
    const [consciousnessLevel, setConsciousnessLevel] = React.useState(allowDefaultValue ? props.consciousnessLevel : null);
    const [score, setScore] = React.useState(props.score);
    const [accepted, setAccepted] = React.useState(props.accepted);
    const [acceptDate, setAcceptDate] = React.useState(props.acceptDate);

    const [consciousnessLevels, setConsciousnessLevels] = React.useState([]);

    // For Mobile view
    useEffect(() => {
        if (props.ipodView && props.status !== obsStatuses.new) {
            setConsciousnessLevel(props.consciousnessLevel);
        }
    }, []);

    useEffect(() => {
        let levels = [];
        if (type === obsTypes.news) {
            levels = [
                {
                    id: 'alert',
                    description: 'Alert',
                    default: true,
                },
                {
                    id: 'newOnsetConfusion',
                    description: 'New Onset Confusion',
                },
                {
                    id: 'respondsToVoice',
                    description: 'Responds to Voice',
                },
                {
                    id: 'respondsToPain',
                    description: 'Responds to Pain',
                },
                {
                    id: 'unresponsive',
                    description: 'Unresponsive',
                },
            ];
        } else if (type === obsTypes.pews) {
            levels = [
                {
                    id: 'alert',
                    description: 'Alert',
                    default: true,
                },
                {
                    id: 'respondsToVoice',
                    description: 'Responds to Voice',
                },
                {
                    id: 'respondsToPain',
                    description: 'Responds to Pain',
                },
                {
                    id: 'unresponsive',
                    description: 'Unresponsive',
                },
            ];
        }
        setConsciousnessLevels(levels);
    }, [type]);

    function setDefaults() {
        let def = _.find(consciousnessLevels, (item) => !!item.default);
        if (def) setConsciousnessLevel(def);
    }

    useEffect(() => {
        if (!consciousnessLevel && allowDefaultValue) {
            setDefaults();
        }
    }, [consciousnessLevels]);

    const clearAccepted = () => {
        setStatus(obsStatuses.new);
        setAccepted(false);
        setAcceptDate(null);
    };

    const resetObs = () => {
        if (!allowDefaultValue) {
            setConsciousnessLevel(null);
        } else {
            setDefaults();
        }
        clearAccepted();
    };

    const acceptObs = () => {
        if (status === obsStatuses.new) setStatus(obsStatuses.completed);
        setAcceptDate(moment());
        setAccepted(true);
    };

    useEffect(() => {
        const checkValue = (!_.isNull(consciousnessLevel) && status === obsStatuses.new) || (status !== obsStatuses.completed && status !== obsStatuses.new);

        if (disableAcceptButtons && checkValue) {
            acceptObs();
        }
    }, [consciousnessLevel, status]);

    const lazyEmitOnChange = React.useMemo(() => {
        return _debounce((value, onChange) => {
            // console.log("lazyEmitOnChange", value);
            onChange(value);
        }, emitChangedWait);
    }, []);

    const emitChanged = () => {
        if (props.onChange) {
            // console.log("emitChanged");
            lazyEmitOnChange(
                {
                    status,
                    consciousnessLevel,
                    score,
                    accepted,
                    acceptDate,
                },
                props.onChange,
            );
        }
    };

    useEffect(() => {
        emitChanged();
    }, [status, consciousnessLevel, score, accepted, acceptDate]);

    const updateScore = () => {
        let newScore = 0;
        if (type === obsTypes.news) {
            if (status === obsStatuses.notRecorded || status === obsStatuses.patientRefused || status === obsStatuses.equipment) {
                newScore = 0;
            } else if (status === obsStatuses.patientCondition) {
                newScore = 3;
            } else {
                if (consciousnessLevel && consciousnessLevel.id !== 'alert') {
                    newScore = 3;
                }
            }
        }
        setScore(newScore);
    };

    useEffect(() => {
        updateScore();
    }, [status, consciousnessLevel]);

    const onLevelChange = (value) => {
        setConsciousnessLevel(value);
        clearAccepted();
    };

    const onStatusChange = (value) => {
        clearAccepted();
        if (value && value !== status) {
            setStatus(value);
        }
    };

    return (
        <div className={classes.obsRoot}>
            <div className={classes.obsContent}>
                <Grid container alignItems={'center'} spacing={2}>
                    <Grid item xs={12} className={classes.component}>
                        <Grid
                            container
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            style={{marginBottom: 15}}
                        >
                            <Grid item xs={12}>
                                <FormLabel className={classes.label}>
                                    {t('Level of Consciousness')}
                                </FormLabel>
                            </Grid>

                            <Grid item xs={12}>
                                <AgyleDictionaryField
                                    onChange={(_, level) => onLevelChange(level)}
                                    options={consciousnessLevels}
                                    value={consciousnessLevel}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    getOptionLabel={(option) => option.description}
                                    extended
                                    noBorder={false}
                                />
                            </Grid>
                        </Grid>
                    </Grid>

                    {type === obsTypes.pews && consciousnessLevel && consciousnessLevel.id !== 'alert' && (
                        <Grid item xs={12}>
                            <InformationBox type={'tip'}>
                                {t('Please complete NEURO observations')}
                            </InformationBox>
                        </Grid>
                    )}

                    {renderAcceptSection({
                        classes,
                        t,
                        hideScore: type === obsTypes.pews,
                        score,
                        status,
                        onStatusChange,
                        resetObs,
                        accepted,
                        acceptObs,
                        acceptDate,
                        acceptDisabled: _.isNull(consciousnessLevel),
                        showStatusNR: type === obsTypes.pews,
                        disableAcceptButtons,
                    })}
                </Grid>
            </div>
        </div>
    );
}

ObsConsciousness.propTypes = {
    type: PropTypes.oneOf([
        obsTypes.news,
        obsTypes.pews,
        obsTypes.neuro,
    ]),
    ageRangeYears: PropTypes.string,
    status: PropTypes.string,
    score: PropTypes.number,
    consciousnessLevel: PropTypes.object,
    accepted: PropTypes.bool,
    acceptDate: PropTypes.instanceOf(moment),
    onChange: PropTypes.func,
};

ObsConsciousness.defaultProps = {
    type: obsTypes.news,
    status: obsStatuses.new,
    score: 0,
    consciousnessLevel: null,
    accepted: false,
    acceptDate: null,
};

export default React.memo(ObsConsciousness);